import { useCallback, useMemo, useState } from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Paper,
	Typography,
} from "@mui/material";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import type { ProductType } from "../models/ProductType";
import { PRODUCT_TYPE_INITIAL_VALUES } from "../models/ProductType";
import { useProductTypes } from "../hooks/useProductTypes";
import {
	useDeleteProductType,
	useUpsertProductType,
} from "../hooks/useProductTypeMutation";
import { ProductTypesTable } from "./ProductTypesTable";
import { ProductTypeDetailViewer } from "./ProductTypeDetailViewer";
import { ProductTypesToolbar } from "./ProductTypesToolbar";
import { SHOW_ONLY_ACTIVES_DEFAULT } from "@/controls/filters/ShowOnlyActivesCheckbox";
import { urlParam, useUrlState, useUrlText } from "@/shared/hooks/useUrlState";
import { useUrlSelection } from "@/shared/hooks/useUrlSelection";

const DEFAULT_LAYOUT = { left: 55, right: 45 };

const URL_SCHEMA = {
	search: urlParam.string(),
	onlyActives: urlParam.bool(SHOW_ONLY_ACTIVES_DEFAULT),
	productTypeId: urlParam.int(),
};

export const ProductTypesManager = () => {
	// Filters and the selected record, all in the query string.
	const [{ search, onlyActives, productTypeId }, setFilters, { dropValues }] =
		useUrlState(URL_SCHEMA);
	// The box types locally and the URL follows once typing stops.
	const [searchText, setSearchText] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null }),
	);
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [productTypeToDelete, setProductTypeToDelete] =
		useState<ProductType | null>(null);
	const [confirmDeactivate, setConfirmDeactivate] = useState<{
		productType: ProductType;
		count: number;
	} | null>(null);

	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "product-types-manager-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const { productTypes, isLoading, error: queryError } = useProductTypes();

	const filteredProductTypes = useMemo(() => {
		const base = onlyActives
			? productTypes.filter((pt) => pt.Active)
			: productTypes;
		if (!searchText) return base;
		const s = searchText.toLowerCase();
		return base.filter(
			(pt) =>
				pt.Name?.toLowerCase().includes(s) ||
				pt.Description?.toLowerCase().includes(s),
		);
	}, [productTypes, searchText, onlyActives]);

	// The id travels and the record is resolved from the list. A record being added has
	// no id yet, so it stays out of the URL.
	const selection = useUrlSelection<ProductType>({
		id: productTypeId,
		setId: (value) => setFilters({ productTypeId: value }),
		dropId: () => dropValues("productTypeId"),
		rows: productTypes,
		ready: !isLoading,
		getId: (record) => record.Id,
	});
	const selectedProductType = selection.current;

	const { upsertProductTypeAsync, isUpserting } = useUpsertProductType({
		onError: (err) => setError(err.message),
	});

	const { deleteProductType, isDeleting } = useDeleteProductType({
		onSuccess: () => {
			setNotification("Product type deleted successfully");
			selection.clear();
			setDeleteModalOpen(false);
			setProductTypeToDelete(null);
		},
		onError: (err) => setError(err.message),
	});

	const handleRowSelected = useCallback(
		(productType: ProductType | null) => {
			if (productType) selection.select(productType);
			else selection.clear();
		},
		[selection],
	);

	const handleDelete = useCallback((productType: ProductType) => {
		setProductTypeToDelete(productType);
		setDeleteModalOpen(true);
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (productTypeToDelete?.Id) {
			deleteProductType(productTypeToDelete.Id);
		}
	}, [productTypeToDelete, deleteProductType]);

	const handleCancelDelete = useCallback(() => {
		setDeleteModalOpen(false);
		setProductTypeToDelete(null);
	}, []);

	const handleAdd = useCallback(() => {
		selection.startNew({ ...PRODUCT_TYPE_INITIAL_VALUES });
	}, [selection]);

	const handleSave = useCallback(
		async (productType: ProductType, force = false) => {
			try {
				const res = await upsertProductTypeAsync({ productType, force });
				if (res.requiresConfirmation) {
					setConfirmDeactivate({
						productType,
						count: res.activeDependents ?? 0,
					});
					return;
				}
				setNotification(
					productType.Id
						? "Product type updated successfully"
						: "Product type created successfully",
				);
				if (res.productType) selection.adopt(res.productType);
			} catch (err) {
				setError((err as Error).message);
			}
		},
		[upsertProductTypeAsync, selection],
	);

	const handleConfirmDeactivate = useCallback(() => {
		if (!confirmDeactivate) return;
		const pt = confirmDeactivate.productType;
		setConfirmDeactivate(null);
		handleSave(pt, true);
	}, [confirmDeactivate, handleSave]);

	const handleCancelDeactivate = useCallback(() => {
		setConfirmDeactivate(null);
		// Revert the detail viewer's optimistic Active checkbox to the stored value. A copy is a
		// new reference, which is what makes the viewer reload the record.
		if (selectedProductType) selection.adopt({ ...selectedProductType });
	}, [selection, selectedProductType]);

	const handleClose = useCallback(() => {
		selection.clear();
	}, [selection]);

	if (queryError) {
		return <ErrorModal error={queryError.message} onHide={() => {}} />;
	}

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
				p: 1,
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
				Product Types
			</Typography>

			<ProductTypesToolbar
				search={searchText}
				onSearchChange={setSearchText}
				onlyActives={onlyActives}
				onOnlyActivesChange={(value) => setFilters({ onlyActives: value })}
				onAdd={handleAdd}
			/>

			<Group
				id="product-types-manager-group"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel id="left" minSize={25} style={{ paddingBottom: 5 }}>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							height: "100%",
							pr: 1,
						}}
					>
						<Paper
							elevation={1}
							sx={{ flex: 1, width: "100%", overflow: "hidden" }}
						>
							{isLoading ? (
								<CenteredLazyLoading />
							) : (
								<ProductTypesTable
									productTypes={filteredProductTypes}
									selectedProductType={
										selectedProductType?.Id ? selectedProductType : null
									}
									onRowSelected={handleRowSelected}
									onDelete={handleDelete}
								/>
							)}
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 6,
							height: 40,
							borderRadius: 3,
							backgroundColor: "divider",
							"&:hover": { backgroundColor: "primary.main" },
						}}
					/>
				</Separator>

				<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ pl: 1, height: "100%" }}>
						<Paper
							elevation={1}
							sx={{
								height: "100%",
								overflowY: "scroll",
								overflowX: "hidden",
							}}
						>
							<ProductTypeDetailViewer
								productType={selectedProductType}
								existingProductTypes={productTypes}
								isSaving={isUpserting || isDeleting}
								onSave={handleSave}
								onClose={handleClose}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<DeleteConfirmModal
				open={deleteModalOpen}
				title="DELETE PRODUCT TYPE"
				itemName={productTypeToDelete?.Name ?? ""}
				isDeleting={isDeleting}
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>

			<Dialog
				open={!!confirmDeactivate}
				onClose={handleCancelDeactivate}
				maxWidth="xs"
			>
				<DialogTitle sx={{ fontWeight: 600 }}>
					Deactivate product type
				</DialogTitle>
				<DialogContent>
					<Typography>
						There are <strong>{confirmDeactivate?.count}</strong> active
						product(s) with this product type. Are you sure you want to
						deactivate it?
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button
						variant="contained"
						onClick={handleCancelDeactivate}
						disabled={isUpserting}
						sx={{
							backgroundColor: "#e0e0e0",
							color: "rgba(0, 0, 0, 0.87)",
							"&:hover": { backgroundColor: "#d5d5d5" },
						}}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="error"
						onClick={handleConfirmDeactivate}
						disabled={isUpserting}
					>
						Deactivate anyway
					</Button>
				</DialogActions>
			</Dialog>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
